import { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { MdAddCircleOutline } from "react-icons/md";
import { FileUpload } from "../../SharedComponents/FileUpload/FileUpload";
import { LARGE_FILE_WARNING_MB } from '../../shared/constants/FileUploadConstants';
import './DocumentUpload.css';

const NOTE_MAX_LENGTH = 500;

const caseInsensitiveCompare = (a, b) => {
  a = a?.toLowerCase?.() || ""; 
  b = b?.toLowerCase?.() || "";
  return a.localeCompare(b);
};

/**
 * DocumentUpload — Modal container for selecting a document type, adding an optional note,
 * and attaching a single file via the FileUpload component.
 *
 * @param {Object[]} requiredList  - Array of required document type objects ({ name, documentRankingId })
 * @param {Object[]} optionalList  - Array of optional document type objects ({ name, documentRankingId })
 * @param {Function} onAddFile     - Callback invoked with { docType, note, file } when the user confirms a file
 * @returns {JSX.Element}
 */
export const DocumentUpload = ({requiredList: _requiredList, optionalList: _optionalList, onAddFile, disabled}) => {
  const [selectedDocType, setSelectedDocType] = useState(null);
  const [note, setNote] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [docSubTypeLookup, setDocSubTypeLookup] = useState({});
  const [requiredList, setRequiredList] = useState(_requiredList || []);
  const [optionalList, setOptionalList] = useState(_optionalList || []);

  useEffect(() => {
    if (_requiredList && _optionalList) {
      // todo: pull from the store
      const subTypeLookup = [..._requiredList, ..._optionalList].reduce((lookup, docTypeObj) => ({
        ...lookup,
        [docTypeObj.name]: docTypeObj,
        [docTypeObj.documentRankingId]: docTypeObj,
      }), {});
      
      setDocSubTypeLookup(subTypeLookup); 
      
      // remove duplicates from both lists in case of bad data
      const reqIds = new Set(_requiredList.map(d => d.documentRankingId));
      const optIds = new Set(_optionalList.map(d => d.documentRankingId).filter(id => !reqIds.has(id)));

      setRequiredList([...reqIds].map(id => subTypeLookup[id]).sort((a, b) => caseInsensitiveCompare(a.name, b.name)));

      // "Other" should always be last in the optional list
      setOptionalList([...optIds].map(id => subTypeLookup[id]).sort((a, b) => {
        if (a.name === "Other") return 1;
        if (b.name === "Other") return -1;
        return caseInsensitiveCompare(a.name, b.name);
      }));
    }
  }, [_requiredList, _optionalList])

  function handleUploadClicked(e) {
    e.preventDefault();
    setShowModal(true);
  }

  function handleDocTypeChanged(documentRankingId) {
    const docType = docSubTypeLookup[documentRankingId];
    setSelectedDocType(docType);

    if (!docType) {
      console.error('Doctype not in lookup:', {documentRankingId, docType, docSubTypeLookup})
    }
  }
  
  function handleNoteChanged(note) {
    setNote(note);
  }

  function handleFileChanged(file) {
    if (file) {
      onAddFile({docType: selectedDocType, note, file});
      reset();
      setShowModal(false);
    }
  }

  function reset() {
    setSelectedDocType(null);
    setNote("")
  }

  function handleCancel() {
    reset();
    setShowModal(false);
  }

  return (
    <div className="upload-docs-row">
      <>
        <button className="add-file-button" onClick={handleUploadClicked} disabled={disabled}>
          <MdAddCircleOutline className="add-file-icon" /> 
          <span>Add Document</span>
        </button>

        <Modal show={showModal} onHide={handleCancel} backdrop="static">
          <Modal.Header closeButton>
            <Modal.Title>Add Document</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="document-uploader" style={{paddingTop: "15px"}}>
              <div className="uploader-control-group">                  
                <h6>Document Type (required)<span className="required">*</span></h6>
                <div className="doctype-select-wrapper">
                  <select
                    className="form-control"
                    value={selectedDocType?.documentRankingId || ""}
                    onChange={event => handleDocTypeChanged(event.target.value)}
                  >
                    <option value="" disabled></option>
                    {requiredList.length ? (
                      <>
                        <optgroup label="Required documents">
                          {requiredList.map((reqDocType, i) => (
                            <option key={i} value={reqDocType.documentRankingId}>
                              {reqDocType.name}
                            </option>
                          ))}
                        </optgroup>
                        {optionalList.length ? (
                          <optgroup label="Optional documents">              
                            {optionalList.map((optDocType, i) => (
                              <option key={i} value={optDocType.documentRankingId}>
                                {optDocType.name}
                              </option>
                            ))}                        
                          </optgroup>
                        ) : (<></>)}
                      </>
                    ) : optionalList.length ? (
                      // if there are no required docs, just show the optional list without the "Optional documents" header
                      optionalList.map((optDocType, i) => (
                        <option key={i} value={optDocType.documentRankingId}>
                          {optDocType.name}
                        </option>
                      ))                     
                    ) : (<></>)}
                  </select> 
                </div>
              </div>
              <div className="uploader-control-group">
                <h6>Note (optional)</h6>
                <input
                  type="text"
                  className="form-control"
                  placeholder=""
                  value={note}
                  maxLength={NOTE_MAX_LENGTH}
                  onChange={event => handleNoteChanged(event.target.value)}
                />
                <div className={`character-count ${note.length >= NOTE_MAX_LENGTH ? 'over-limit' : ''}`}>
                  {note.length} / {NOTE_MAX_LENGTH} characters
                </div>
              </div>
              <div style={{
                backgroundColor: '#fff3cd',
                border: '1px solid #ffc107',
                borderRadius: '4px',
                padding: '12px 16px',
                margin: '5px 20px 0',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span style={{ fontSize: '16px', color: '#856404' }}>⚠️</span>
                <span style={{ color: '#856404', fontSize: '14px' }}>
                  Files over {LARGE_FILE_WARNING_MB} MB may take longer. Please consider splitting the file to improve upload speed.
                </span>
              </div>
              <div className="uploader-file-drop">
                <FileUpload 
                  multiple={false}
                  disableContinue={!selectedDocType}
                  onCancel={handleCancel} 
                  onFilesAdded={files => handleFileChanged(files[0])} 
                />
              </div>
            </div>
          </Modal.Body>
        </Modal>
      </>
    </div>
  )
};
